// src/ReferenceUploads.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  LinearProgress,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete from '@mui/material/Autocomplete';      // ← new
import { AuthContext } from '../context/AuthContext';
import { baseUrl } from "../api";

export default function ReferenceUploads() {
  const { user } = useContext(AuthContext);

  const axiosInstance = useMemo(() => {
    const inst = axios.create({ baseURL: baseUrl });
    inst.interceptors.request.use(async (config) => {
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return inst;
  }, [user]);

  const [file, setFile]               = useState(null);
  const [mimeType, setMimeType]       = useState('');
  const [metadataTag, setMetadataTag] = useState('my-rag');
  const [tags, setTags]               = useState([]);      // ← loaded tags
  const [uploadLoading, setUploadLoading]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [documents, setDocuments]     = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [error, setError]             = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Basic fallback map for common extensions
  const extensionToMime = {
    pdf: 'application/pdf',
    txt: 'text/plain',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    csv: 'text/csv',
  };

  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      const resp = await axiosInstance.get('/rag/documents');
      setDocuments(resp.data);
    } catch {
      setError('Failed to load documents.');
    } finally {
      setDocsLoading(false);
    }
  };

  // fetch existing metadata-tags
  const fetchTags = async () => {
    try {
      const resp = await axiosInstance.get('/rag/metadata-tags');
      setTags(resp.data);
    } catch {
      console.error('Could not load metadata tags');
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchTags();      // ← load tags once
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    setFile(selected);
    setSuccessMessage('');
    setError('');
    if (selected) {
      let detected = selected.type;
      if (!detected) {
        const ext = selected.name.split('.').pop().toLowerCase();
        detected = extensionToMime[ext] || '';
      }
      setMimeType(detected);
    } else {
      setMimeType('');
    }
  };

  const handleUpload = async () => {
    if (!file || !mimeType) {
      setError('Please select a file and specify its MIME type.');
      return;
    }
    setUploadLoading(true);
    setUploadProgress(0);
    setError('');
    setSuccessMessage('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mime_type', mimeType);
    formData.append('metadata_tag', metadataTag);

    try {
      await axiosInstance.post('/rag/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ev => setUploadProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      setSuccessMessage('Upload successful!');
      setFile(null);
      setMimeType('');
      fetchDocuments();
      fetchTags();   // refresh tags in case a brand-new one was just created
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axiosInstance.delete(`/rag/documents/${id}`);
      setSuccessMessage('Document deleted.');
      fetchDocuments();
    } catch {
      setError('Failed to delete document.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Reference Uploads
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <input
            accept="*/*"
            id="rag-file-input"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={uploadLoading}
          />
          <label htmlFor="rag-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFileIcon />}
              disabled={uploadLoading}
            >
              Choose File
            </Button>
          </label>
          {file && <Typography sx={{ ml: 2 }} noWrap>{file.name}</Typography>}
        </Box>

        {/* ← max file size note */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Max RAG upload file size: 30&nbsp;MB
        </Typography>

        <TextField
          fullWidth
          label="MIME Type"
          placeholder="e.g. application/pdf"
          value={mimeType}
          onChange={(e) => { setMimeType(e.target.value); setError(''); }}
          disabled={uploadLoading}
          sx={{ mb: 3 }}
        />

        {/* ← REPLACE TextField with Autocomplete */}
        <Autocomplete
          freeSolo
          options={tags}
          value={metadataTag}
          onChange={(_, newValue) => setMetadataTag(newValue || '')}
          onInputChange={(_, newInput) => setMetadataTag(newInput)}
          disabled={uploadLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Metadata Tag"
              placeholder="Select or type a new tag"
              sx={{ mb: 3 }}
            />
          )}
        />

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploadLoading}
          fullWidth
        >
          Upload to RAG Store
        </Button>

        {uploadLoading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Ingested Documents
        </Typography>

        {docsLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          : (
            documents.length === 0
              ? <Typography>No documents uploaded yet.</Typography>
              : (
                <List>
                  {documents.map((doc) => (
                    <React.Fragment key={doc.id}>
                      <ListItem
                        secondaryAction={
                          <IconButton edge="end" onClick={() => handleDelete(doc.id)}>
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={doc.file_name}
                          secondary={
                            <>
                              <Typography component="div" variant="body2" color="text.secondary">
                                <strong>ID:</strong> {doc.id}
                              </Typography>
                              <Typography component="div" variant="body2" color="text.secondary">
                                <strong>Uploaded:</strong> {new Date(doc.uploaded_at).toLocaleString()}
                                &nbsp;—&nbsp;
                                <strong>Chunks:</strong> {doc.num_chunks}
                              </Typography>
                              <Typography component="div" variant="body2" color="text.secondary">
                                <strong>Tag:</strong> {doc.tag || '—'}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )
          )
        }
      </Paper>
    </Container>
  );
}
